import { useEffect, useMemo, useRef, useState } from "react";
import { Add, Download, PlaylistPlay, Upload } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  useTheme,
} from "@mui/material";
import { v4 as uuidv4 } from "uuid";
import { useNotification } from "../../../../../componentes/notificationProvider";
import NewAlertModal from "../../../../../componentes/newAlertModal";
import FlowHeader from "../../components/flowHeader";
import FlowTable from "../../components/flowTable";
import SectionBar from "../../components/sectionBar";
import { useTabStyles } from "../../components/styles";
import { routerMassColumns } from "../columns";
import { hasErrors, validateRouter } from "../validation";
import { downloadCsvTemplate, routersFromCsv } from "./csv";

import {
  allTestsPassed,
  DEFAULT_SNMP_PORT,
  DEFAULT_SNMP_VERSION,
  emptyRouter,
  emptyTestsLocal,
  Router,
  RouterMassRow,
  RouterPayload,
  RouterTestType,
  ROUTER_FIELDS,
  TestsLocalState,
} from "../types";
import api from "../../../../../services/api";

const TEST_CHUNK_SIZE = 4;

const chunk = <T,>(items: T[], size: number): T[][] =>
  items.reduce<T[][]>((chunks, item, index) => {
    if (index % size === 0) chunks.push([]);
    chunks[chunks.length - 1].push(item);
    return chunks;
  }, []);

const isNewRow = (row: RouterMassRow) => row?._isNew;

const isEditedRow = (row: RouterMassRow) => row?._isEdited;

const isPendingRow = (row: RouterMassRow) => row?._isNew || row?._isEdited;

const hasChanges = (row: RouterMassRow, original?: RouterPayload) =>
  Boolean(original) &&
  ROUTER_FIELDS.some(
    (field) =>
      (row[field] ?? "").toString().trim() !==
      (original?.[field] ?? "").toString().trim(),
  );

const toPayload = (row: RouterMassRow): RouterPayload => ({
  ...ROUTER_FIELDS.reduce(
    (payload, field) => ({
      ...payload,
      [field]: (row[field] ?? "").toString().trim(),
    }),
    { ...emptyRouter },
  ),
  fabricante: row.fabricante?.trim(),
  modelo: row.modelo?.trim(),
});

const newRow = (router: RouterPayload = emptyRouter): RouterMassRow => {
  const data = { ...emptyRouter, ...router };
  return {
    ...data,
    id: uuidv4(),
    _isNew: true,
    _isEdited: false,
    selected: false,
    Status: { ...emptyTestsLocal },
    _errors: validateRouter(data),
  };
};

const existingRow = (router: Router): RouterMassRow => {
  const { id, ...rest } = router;
  const data = { ...emptyRouter, ...rest };
  return {
    ...data,
    id: uuidv4(),
    dbId: id,
    _original: { ...data },
    _isNew: false,
    _isEdited: false,
    selected: false,
    Status: { ...emptyTestsLocal },
    _errors: validateRouter(data),
  };
};

const statusFromResult = (result: any): TestsLocalState =>
  (["ping", "snmp", "pacotes"] as RouterTestType[]).reduce(
    (status, type) => {
      status[type] = result?.[type] ? Boolean(result[type].success) : false;
      return status;
    },
    { ...emptyTestsLocal },
  );

export interface MassRoutersProps {
  routers: Router[];
  search: string;
  onSearchChange: (value: string) => void;
  onBack: () => void;
  onSaved: () => Promise<void> | void;
}

export default function MassRouters(props: MassRoutersProps) {
  const { routers, search, onSearchChange, onBack, onSaved } = props;

  const [rows, setRows] = useState<RouterMassRow[]>([]);
  const [isTesting, setIsTesting] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [tested, setTested] = useState({ done: 0, total: 0 });
  const [tableKey, setTableKey] = useState<number>(0);

  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [openBackModal, setOpenBackModal] = useState<boolean>(false);

  const theme = useTheme();
  const styles = useTabStyles(theme);
  const notify = useNotification();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const busy = isTesting || isSaving;

  const refreshRows = () => setRows((prev) => [...prev]);

  useEffect(() => {
    if (search.trim()) onSearchChange("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setRows((prev) => {
      const anteriores = new Map(
        prev
          .filter((row) => !isNewRow(row))
          .map((row) => [String(row.dbId), row]),
      );

      const existentes = routers.map((router) => {
        const atual = existingRow(router);
        const anterior = anteriores.get(String(router.id));
        if (!anterior) return atual;

        return {
          ...anterior,
          _original: atual._original,
          _isEdited: hasChanges(anterior, atual._original),
        };
      });

      return [...existentes, ...prev.filter(isNewRow)];
    });
    setTableKey((key) => key + 1);
  }, [routers]);

  const rowsForDisplay = useMemo(() => {
    if (!search.trim()) return rows;

    const searchLower = search.toLowerCase();
    return rows.filter((row) =>
      ROUTER_FIELDS.some((field) =>
        String(row[field] ?? "")
          .toLowerCase()
          .includes(searchLower),
      ),
    );
  }, [rows, search]);

  const selectedRows = useMemo(
    () => rows.filter((row) => row.selected),
    [rows],
  );
  const pendingRows = useMemo(() => rows.filter(isPendingRow), [rows]);
  const approvedRows = useMemo(
    () =>
      pendingRows.filter(
        (row) => allTestsPassed(row.Status) && !hasErrors(row._errors),
      ),
    [pendingRows],
  );
  const invalidCount = useMemo(
    () => rows.filter((row) => hasErrors(row._errors)).length,
    [rows],
  );

  const handleClearSelection = () => {
    rows.forEach((row) => {
      row.selected = false;
    });
    refreshRows();
    setTableKey((key) => key + 1);
  };

  const handleAddRow = () => {
    setRows((prev) => [...prev, newRow()]);
    setTableKey((key) => key + 1);
  };

  const handleCellChange = (row: RouterMassRow) => {
    row._errors = validateRouter(row);
    row.Status = { ...emptyTestsLocal };
    if (!row?._isNew) row._isEdited = hasChanges(row, row._original);
    refreshRows();
  };

  const handleImportCsv = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".csv")) {
      notify("Apenas arquivos .csv são permitidos.", "error");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const imported = routersFromCsv(String(reader.result ?? ""));
        if (imported.length === 0) {
          notify("Nenhum roteador encontrado no arquivo.", "warning");
          return;
        }

        setRows((prev) => [
          ...prev,
          ...imported.map((router) => newRow(router)),
        ]);
        setTableKey((key) => key + 1);
        notify(
          `${imported.length} roteador(es) importado(s) do CSV.`,
          "success",
        );
      } catch (error: any) {
        notify(error?.message || "Erro ao ler o arquivo CSV", "error");
      }
    };
    reader.onerror = () => notify("Erro ao ler o arquivo CSV", "error");
    reader.readAsText(file, "UTF-8");
  };

  const handleRunTests = async () => {
    const scope = selectedRows.length > 0 ? selectedRows : rows;
    const testable = scope.filter((row) => !hasErrors(row._errors));

    if (testable.length === 0) {
      notify(
        "Corrija os campos obrigatórios antes de testar os roteadores.",
        "warning",
      );
      return;
    }

    if (testable.length < scope.length) {
      notify(
        `${scope.length - testable.length} roteador(es) com dados inválidos ficaram de fora do teste.`,
        "warning",
        6000,
      );
    }

    const jobs = [
      ...chunk(
        testable.filter((row) => !isPendingRow(row)),
        TEST_CHUNK_SIZE,
      ).map((lote) => ({ porId: true, lote })),
      ...chunk(testable.filter(isPendingRow), TEST_CHUNK_SIZE).map((lote) => ({
        porId: false,
        lote,
      })),
    ];

    setIsTesting(true);
    setTested({ done: 0, total: testable.length });

    for (const { porId, lote } of jobs) {
      lote.forEach((row) => {
        row._loading = true;
        row.Status = { ...emptyTestsLocal };
      });
      refreshRows();

      try {
        const { data } = porId
          ? await api.post("/easyFlow2.0/routers/test/batch", {
              ids: lote.map((row) => row.dbId),
            })
          : await api.post(
              "/easyFlow2.0/routers/test",
              lote.map((row) => ({
                ipRouter: row.sourceIp?.trim(),
                snmpIp: row.snmpIp?.trim(),
                snmpCommunity: row.snmpCommunity?.trim(),
                snmpPort: Number(row.snmpPort) || DEFAULT_SNMP_PORT,
                snmpVersion: DEFAULT_SNMP_VERSION,
                type: "all",
              })),
            );

        const resultados: any[] = data?.data?.resultados ?? [];
        const porRouterId = new Map(
          resultados
            .filter((resultado) => resultado?.routerId !== undefined)
            .map((resultado) => [String(resultado.routerId), resultado]),
        );

        lote.forEach((row, index) => {
          row.Status = statusFromResult(
            porRouterId.get(String(row.dbId)) ?? resultados[index],
          );
        });
      } catch (error: any) {
        console.error(error);
        notify(
          error?.response?.data?.error ||
            error?.message ||
            "Falha ao testar o lote de roteadores.",
          "error",
          8000,
        );
      } finally {
        lote.forEach((row) => {
          row._loading = false;
        });
        refreshRows();
        setTested((prev) => ({ ...prev, done: prev.done + lote.length }));
      }
    }

    const aprovados = testable.filter((row) => allTestsPassed(row.Status));

    setIsTesting(false);
    notify(
      `${testable.length} roteador(es) testado(s) — ${aprovados.length} aprovado(s) nos 3 testes.`,
      aprovados.length === testable.length ? "success" : "warning",
      8000,
    );
  };

  const handleSave = async () => {
    if (approvedRows.length === 0) {
      notify(
        "Nenhum roteador aprovado nos 3 testes. Execute os testes antes de salvar.",
        "warning",
        6000,
      );
      return;
    }

    const paraCriar = approvedRows.filter(isNewRow);
    const paraEditar = approvedRows.filter((row) => !isNewRow(row));
    const semTeste = pendingRows.length - approvedRows.length;

    try {
      setIsSaving(true);

      if (paraCriar.length > 0) {
        await api.post("/easyFlow2.0/routers", paraCriar.map(toPayload));
      }

      if (paraEditar.length > 0) {
        await api.put(
          "/easyFlow2.0/routers",
          paraEditar.map((row) => ({ id: row.dbId, ...toPayload(row) })),
        );
      }

      const criados = new Set(paraCriar.map((row) => row.id));
      setRows((prev) => prev.filter((row) => !criados.has(row.id)));

      notify(
        [
          paraCriar.length > 0 ? `${paraCriar.length} cadastrado(s)` : "",
          paraEditar.length > 0 ? `${paraEditar.length} atualizado(s)` : "",
        ]
          .filter(Boolean)
          .join(" e ") + " com sucesso!",
        "success",
        6000,
      );

      if (semTeste > 0) {
        notify(
          `${semTeste} roteador(es) não foram salvos: ainda não passaram nos 3 testes.`,
          "warning",
          8000,
        );
      }

      await onSaved();

      if (semTeste === 0) onBack();
    } catch (error: any) {
      console.error(error);
      notify(
        error?.response?.data?.error ||
          error?.message ||
          "Não foi possível salvar os roteadores.",
        "error",
        8000,
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteSelected = async () => {
    const naPlanilha = selectedRows.filter((row) => !isNewRow(row));
    const ids = new Set(selectedRows.map((row) => row.id));

    try {
      setIsSaving(true);

      if (naPlanilha.length > 0) {
        await api.delete("/easyFlow2.0/routers", {
          data: { ids: naPlanilha.map((row) => row.dbId) },
        });
      }

      setRows((prev) => prev.filter((row) => !ids.has(row.id)));
      setTableKey((key) => key + 1);

      notify(
        `${selectedRows.length} roteador(es) removido(s)${
          naPlanilha.length > 0 ? " da planilha" : ""
        }.`,
        "success",
        4000,
      );

      if (naPlanilha.length > 0) await onSaved();
    } catch (error: any) {
      console.error(error);
      notify(
        error?.response?.data?.error ||
          error?.message ||
          "Não foi possível remover os roteadores.",
        "error",
        8000,
      );
    } finally {
      setIsSaving(false);
      setOpenDeleteModal(false);
    }
  };

  const handleRequestBack = () => {
    if (pendingRows.length > 0) {
      setOpenBackModal(true);
      return;
    }
    onBack();
  };

  return (
    <>
      <FlowHeader
        isEditable
        search={search}
        onSearchChange={onSearchChange}
        searchPlaceholder="Pesquisar roteadores"
        savingChanges={busy}
        disableSave={approvedRows.length === 0}
        saveLabel="Salvar aprovados"
        onCancelEditMode={handleRequestBack}
        onSaveChanges={handleSave}
        startContent={
          <Stack direction="row" alignItems="center" gap={0.5} flexWrap="wrap">
            <Chip size="small" label={`${rows.length} linha(s)`} />
            <Chip
              size="small"
              color="success"
              variant="outlined"
              label={`${approvedRows.length} aprovado(s)`}
            />

            {pendingRows.length > 0 && (
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                label={`${pendingRows.length} pendente(s)`}
              />
            )}
            {invalidCount > 0 && (
              <Chip
                size="small"
                color="error"
                variant="outlined"
                label={`${invalidCount} com erro`}
              />
            )}

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            <Tooltip title="Baixar o modelo de planilha para a importação">
              <span>
                <Button
                  size="small"
                  variant="text"
                  disabled={busy}
                  onClick={downloadCsvTemplate}
                  startIcon={<Download fontSize="small" />}
                >
                  Baixar template
                </Button>
              </span>
            </Tooltip>
          </Stack>
        }
        extraActions={
          <Stack direction="row" alignItems="center" gap={0.5} flexWrap="wrap">
            <Tooltip title="Importar de um arquivo CSV">
              <span>
                <IconButton
                  size="small"
                  sx={styles.iconButtonAction}
                  disabled={busy}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <input
              type="file"
              hidden
              accept=".csv"
              ref={fileInputRef}
              onChange={handleImportCsv}
            />

            <Tooltip title="Adicionar linha">
              <span>
                <IconButton
                  size="small"
                  sx={styles.iconButtonAction}
                  disabled={busy}
                  onClick={handleAddRow}
                >
                  <Add fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip
              title={
                selectedRows.length > 0
                  ? `Testar ${selectedRows.length} roteador(es) selecionado(s)`
                  : "Testar todos os roteadores da tabela"
              }
            >
              <span>
                <IconButton
                  size="small"
                  sx={styles.iconButtonAction}
                  disabled={busy || rows.length === 0}
                  onClick={handleRunTests}
                >
                  <PlaylistPlay fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        }
      />

      <Box sx={styles.boxTable}>
        <FlowTable<RouterMassRow>
          key={`routersMass-${tableKey}`}
          id="routersMassFlow"
          rows={rowsForDisplay}
          columns={routerMassColumns}
          isEditable={!busy}
          selectable
          onSelectionChange={refreshRows}
          onCellChange={handleCellChange}
          rowClassName={(row) => (isNewRow(row) ? "row-new" : undefined)}
        />
      </Box>

      <SectionBar
        selectedCount={selectedRows.length}
        label="Roteadores selecionados"
        disabled={busy}
        onRunTests={handleRunTests}
        onDelete={() => setOpenDeleteModal(true)}
        onClearSelection={handleClearSelection}
      />

      <NewAlertModal
        openModal={openDeleteModal}
        setOpenModal={setOpenDeleteModal}
        textContent={`Tem certeza que deseja excluir ${selectedRows.length} roteador(es)?`}
        handleButton={handleDeleteSelected}
        severity={"error"}
        singleButton={false}
      />

      <NewAlertModal
        openModal={openBackModal}
        setOpenModal={setOpenBackModal}
        textContent="Existem alterações não salvas. Se sair agora, essas alterações serão perdidas."
        buttonText="Descartar"
        buttonCancelText="Cancelar"
        handleButton={() => {
          setOpenBackModal(false);
          onBack();
        }}
        severity={"warning"}
        singleButton={false}
      />
    </>
  );
}
